import type { I18nStrings, ConversationMode } from '@bitio-builder/shared';

interface ModeSelectorProps {
  strings: I18nStrings;
  onSelectMode: (mode: ConversationMode) => void;
  onLeaveMessage: () => void;
  isLoading?: boolean;
  isWithinOfficeHours: boolean;
}

const containerStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 24px',
  textAlign: 'center',
};

const titleStyle: Record<string, string | number> = {
  fontSize: '18px',
  fontWeight: 600,
  color: 'var(--bb-foreground, #1A1C17)',
  marginBottom: '24px',
};

const buttonContainerStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
  maxWidth: '280px',
};

const buttonBaseStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  padding: '16px 20px',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 150ms ease',
  textAlign: 'center',
  border: 'none',
};

const primaryButtonStyle: Record<string, string | number> = {
  ...buttonBaseStyle,
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
};

const secondaryButtonStyle: Record<string, string | number> = {
  ...buttonBaseStyle,
  background: 'white',
  color: 'var(--bb-foreground, #1A1C17)',
  border: '1px solid var(--bb-border, #E5E7EB)',
};

const buttonTitleStyle: Record<string, string | number> = {
  fontSize: '15px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const buttonDescStyle: Record<string, string | number> = {
  fontSize: '13px',
  opacity: 0.8,
};

const loadingStyle: Record<string, string | number> = {
  opacity: 0.6,
  pointerEvents: 'none',
};

const orLinkStyle: Record<string, string | number> = {
  marginTop: '16px',
  fontSize: '13px',
  color: 'var(--bb-muted, #6B7280)',
  cursor: 'pointer',
  textDecoration: 'underline',
  background: 'none',
  border: 'none',
  fontFamily: 'inherit',
};

const offlineIndicatorStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '12px',
  color: 'var(--bb-muted, #6B7280)',
  marginBottom: '20px',
};

const ChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const EmailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const OfflineIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '12px', height: '12px' }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

export function ModeSelector({ strings, onSelectMode, onLeaveMessage, isLoading, isWithinOfficeHours }: ModeSelectorProps) {
  // Outside office hours: show leave message as only option
  if (!isWithinOfficeHours) {
    return (
      <div style={containerStyle}>
        <h2 style={titleStyle}>{strings.modeSelector.title}</h2>

        <div style={offlineIndicatorStyle}>
          <OfflineIcon />
          <span>{strings.leaveMessage.outsideHoursTitle}</span>
        </div>

        <div style={buttonContainerStyle}>
          <button
            style={{ ...primaryButtonStyle, ...(isLoading ? loadingStyle : {}) }}
            onClick={onLeaveMessage}
            disabled={isLoading}
          >
            <span style={buttonTitleStyle}>
              <EmailIcon />
              {strings.leaveMessage.title}
            </span>
            <span style={buttonDescStyle}>{strings.leaveMessage.outsideHoursSubtitle}</span>
          </button>
        </div>
      </div>
    );
  }

  // Within office hours: show chat as primary, with subtle leave message link
  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>{strings.modeSelector.title}</h2>

      <div style={buttonContainerStyle}>
        <button
          style={{ ...primaryButtonStyle, ...(isLoading ? loadingStyle : {}) }}
          onClick={() => onSelectMode('slack')}
          disabled={isLoading}
        >
          <span style={buttonTitleStyle}>
            <ChatIcon />
            {strings.modeSelector.chatWithUs}
          </span>
          <span style={buttonDescStyle}>{strings.modeSelector.chatWithUsDesc}</span>
        </button>
      </div>

      <button
        style={orLinkStyle}
        onClick={onLeaveMessage}
        disabled={isLoading}
      >
        {strings.leaveMessage.orLeaveMessage}
      </button>
    </div>
  );
}
